"use client";

import {
  Area,
  CartesianGrid,
  AreaChart as RechartsAreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { type ChartConfig, ChartContainer } from "./ChartContainer";
import { ChartTooltipContent } from "./ChartTooltipContent";

export type AreaChartData = Record<string, string | number>;

export type AreaChartProps = {
  /** Chart data (array of objects, e.g. [{ month: "January", desktop: 186 }]) */
  data: AreaChartData[];
  /** Chart configuration */
  config: ChartConfig;
  /** Data key for X axis (e.g. "month") */
  xDataKey: string;
  /** Data key(s) for area value(s) (e.g. "desktop" or ["desktop", "mobile"]) */
  valueDataKey: string | string[];
  /** X axis tick formatter (e.g. show first 3 chars) */
  xAxisTickFormatter?: (value: string) => string;
  /** Margin around chart */
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  /** Fill opacity for area (default: 0.4) */
  fillOpacity?: number;
  /** Stack areas (default: false) */
  stacked?: boolean;
  /** Show vertical grid lines (default: false) */
  verticalGrid?: boolean;
  /** Show tooltip (default: true) */
  showTooltip?: boolean;
  /** Chart container className */
  className?: string;
  /** Fixed chart height (px). Prefer setting when parent layout has no explicit height — otherwise ResponsiveContainer can render at 0px. */
  chartHeight?: number;
};

export function AreaChart({
  data,
  config,
  xDataKey,
  valueDataKey,
  xAxisTickFormatter,
  margin = { left: 12, right: 12 },
  fillOpacity = 0.4,
  stacked = false,
  verticalGrid = false,
  showTooltip = true,
  className,
  chartHeight,
}: AreaChartProps) {
  const valueKeys = Array.isArray(valueDataKey) ? valueDataKey : [valueDataKey];

  return (
    <ChartContainer
      config={config}
      className={className}
      style={chartHeight != null ? { height: chartHeight } : undefined}
    >
      <ResponsiveContainer
        width="100%"
        height={chartHeight ?? "100%"}
        minHeight={chartHeight == null ? 200 : undefined}
      >
        <RechartsAreaChart data={data} margin={margin} accessibilityLayer>
          <CartesianGrid vertical={verticalGrid} strokeDasharray="3 3" />
          <XAxis
            dataKey={xDataKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={xAxisTickFormatter ?? ((v) => String(v))}
          />
          {showTooltip && (
            <Tooltip
              cursor={false}
              content={<ChartTooltipContent config={config} indicator="dot" />}
            />
          )}
          {valueKeys.map((key, index) => {
            const color =
              config[key]?.color ??
              `var(--chart-${((index % 5) + 1) as 1 | 2 | 3 | 4 | 5})`;
            return (
              <Area
                key={key}
                dataKey={key}
                type="natural"
                fill={color}
                fillOpacity={fillOpacity}
                stroke={color}
                stackId={stacked ? "stack" : undefined}
              />
            );
          })}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}
